import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, Milk, Wheat, Cookie, Sparkles, Home as HomeIcon, ArrowRight, Search, Store, ShieldCheck, TrendingUp, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import ProductCard from "@/components/public/ProductCard";

const catIcons = {
  "Fruits & Vegetables": Apple, "Dairy & Bakery": Milk, "Staples & Pulses": Wheat,
  "Snacks & Beverages": Cookie, "Personal Care": Sparkles, "Household": HomeIcon,
};

export default function Home() {
  const [cats, setCats] = useState([]);
  const [deals, setDeals] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/products?deal=true").then((r) => setDeals(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Supermarket aisle" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 px-3 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> 40+ stores across India
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Fresh groceries,<br /><span className="text-yellow-400">close to home.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-lg">
              Shop everyday essentials from your neighbourhood D K BAZAAR — or own one. Build your cart, place an order request, and pick it up fresh.
            </p>
            <form onSubmit={(e) => e.preventDefault()} className="mt-8 flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fruits, dairy, staples…"
                  className="pl-9 bg-white border-0 h-12" data-testid="hero-search" />
              </div>
              <Button asChild className="h-12 bg-yellow-400 hover:bg-yellow-500 text-slate-900 px-6">
                <Link to={`/catalogue${q ? `?q=${encodeURIComponent(q)}` : ""}`} data-testid="hero-search-btn">Shop</Link>
              </Button>
            </form>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700 text-white">
                <Link to="/franchise" data-testid="hero-franchise-btn">Own a Franchise <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <Link to="/stores">Find a Store</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[[Store, "40+ Stores", "Growing every month"], [ShieldCheck, "Trusted Quality", "Farm-fresh sourcing"], [Truck, "Pickup & Delivery", "Fast fulfilment"], [TrendingUp, "Franchise ROI", "Proven retail model"]].map(([Icon, t, s], i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-yellow-100 dark:bg-yellow-950 text-yellow-600"><Icon className="w-5 h-5" /></span>
              <div><p className="font-heading font-semibold text-sm">{t}</p><p className="text-xs text-muted-foreground">{s}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-yellow-700">Browse</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-1">Shop by category</h2>
          </div>
          <Link to="/catalogue" className="text-sm font-medium text-yellow-700 hover:underline flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {cats.map((c) => {
            const Icon = catIcons[c.name] || Apple;
            return (
              <Link key={c.name} to={`/catalogue?category=${encodeURIComponent(c.name)}`} data-testid={`category-${c.name}`}
                className="group rounded-2xl border border-slate-200/80 dark:border-slate-800 p-5 text-center hover:border-yellow-400 hover:shadow-lg transition-all bg-white dark:bg-slate-900">
                <span className="mx-auto grid place-items-center w-14 h-14 rounded-2xl bg-yellow-100 dark:bg-yellow-950 text-yellow-600 group-hover:bg-yellow-400 group-hover:text-slate-900 transition-colors"><Icon className="w-7 h-7" /></span>
                <p className="mt-3 font-heading font-semibold text-sm">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.count} items</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Deals */}
      <section className="bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">Today's Deals</span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-1">Fresh savings</h2>
            </div>
            <Link to="/catalogue" className="text-sm font-medium text-yellow-700 hover:underline flex items-center gap-1">All products <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {deals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Franchise CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-yellow-400 text-slate-900 p-10 lg:p-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold">Build your business with D K BAZAAR</h2>
            <p className="mt-4 text-slate-800 text-lg max-w-md">Join India's fastest-growing grocery franchise network. Proven model, central supply chain, full onboarding support.</p>
            <Button asChild size="lg" className="mt-6 bg-slate-900 text-white hover:bg-slate-800">
              <Link to="/franchise" data-testid="cta-franchise-btn">Start Your Application <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
          <img src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
            alt="Stocked store shelves" className="rounded-2xl object-cover w-full h-64 lg:h-80" />
        </div>
      </section>
    </div>
  );
}
